import React, { useEffect } from 'react';
import { Button } from '@mui/material';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import Strings from '../../../../i18n';
import { ErrorText } from '../../../../components/auth/Profile/completeProfileStyles';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledTitleSideBar
} from '../../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { SideBarEditablePropertyItem } from '../../../../components/commons/SideBarComponents/EditablePropertyItem/sideBarEditablePropertyItem';
import { SideBarDeleteActionWithPopup } from '../../../../components/commons/SideBarComponents/DeleteActionWithPopup/sideBarDeleteActionWithPopup';
import { EditPartNumberTemplateProps } from './editPartNumberTemplateProps';
import { partNumberAction } from '../../../../utils/validators';
import {
  useDeletePartNumberMutation,
  useUpdatePartNumberMutation
} from '../../../../services/manufacture/manufactureService';

export const EditPartNumberSideBar = ({
  partNumber,
  reloadTable,
  setOpenSideBar,
  setUsersSelected,
  openRightSideBarForMethod
}: EditPartNumberTemplateProps) => {
  const [updatePartNumber, { isSuccess }] = useUpdatePartNumberMutation();
  const [deletePartNumber, { isSuccess: isSuccessDelete }] =
    useDeletePartNumberMutation();

  useEffect(() => {
    if (isSuccess) {
      toast.success('Data was updated successfully');
      reloadTable();
      setOpenSideBar(false);
    }
  }, [isSuccess]);

  const deletePartNumberAction = () => {
    if (partNumber?.id) {
      deletePartNumber(partNumber.id);
    }
  };

  useEffect(() => {
    if (isSuccessDelete) {
      toast.success('Data was deleted successfully');
      reloadTable();
      setOpenSideBar(false);
      setUsersSelected([]);
    }
  }, [isSuccessDelete]);

  return (
    partNumber && (
      <Formik
        enableReinitialize
        validationSchema={partNumberAction}
        initialValues={{
          partNumber: partNumber?.partNumber,
          series: partNumber.series,
          product: partNumber.product,
          model: partNumber.model,
          sku: partNumber.sku,
          startSerialNumber: partNumber.startSerialNumber,
          description: partNumber.description
        }}
        onSubmit={(values) => {
          updatePartNumber({
            partNumber: values.partNumber,
            series: values.series,
            product: values.product,
            model: values.model,
            sku: values.sku,
            startSerialNumber: values.startSerialNumber,
            description: values.description,
            id: partNumber.id
          });
        }}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          isValid,
          setFieldTouched,
          errors,
          touched
        }) => (
          <>
            <StyledContainerItems>
              <StyledTitleSideBar>
                {Strings.manufacture.partNumber.title}
              </StyledTitleSideBar>
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.manufacture.partNumber.create.partNumber,
                  type: 'inputText',
                  currentValue: values.partNumber,
                  nameAndId: 'partNumber',
                  onChange: handleChange('partNumber'),
                  onBlur: () => setFieldTouched('partNumber')
                }}
              />
              {touched.partNumber && errors.partNumber && (
                <ErrorText>{String(errors.partNumber)}</ErrorText>
              )}
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.manufacture.partNumber.create.product,
                  type: 'inputText',
                  currentValue: values.product,
                  nameAndId: 'product',
                  onChange: handleChange('product'),
                  onBlur: () => setFieldTouched('product')
                }}
              />
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.manufacture.partNumber.create.series,
                  type: 'inputText',
                  currentValue: values.series,
                  nameAndId: 'series',
                  onChange: handleChange('series'),
                  onBlur: () => setFieldTouched('series')
                }}
              />
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.manufacture.partNumber.create.model,
                  type: 'inputText',
                  currentValue: values.model,
                  nameAndId: 'model',
                  onChange: handleChange('model'),
                  onBlur: () => setFieldTouched('model')
                }}
              />
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.manufacture.partNumber.create.sku,
                  type: 'inputText',
                  currentValue: values.sku,
                  nameAndId: 'sku',
                  onChange: handleChange('sku'),
                  onBlur: () => setFieldTouched('sku')
                }}
              />
              {touched.sku && errors.sku && (
                <ErrorText>{String(errors.sku)}</ErrorText>
              )}
              <SideBarEditablePropertyItem
                item={{
                  label:
                    Strings.manufacture.partNumber.create.startSerialNumber,
                  type: 'inputText',
                  currentValue: values.startSerialNumber,
                  nameAndId: 'startSerialNumber',
                  onChange: handleChange('startSerialNumber'),
                  onBlur: () => setFieldTouched('startSerialNumber')
                }}
              />
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.manufacture.partNumber.create.description,
                  type: 'inputText',
                  currentValue: values.description,
                  nameAndId: 'description',
                  onChange: handleChange('description'),
                  onBlur: () => setFieldTouched('description')
                }}
              />
              <SideBarDeleteActionWithPopup
                onDelete={() => deletePartNumberAction()}
                titleConfirmation={
                  Strings.manufacture.partNumber.create.deleteUserTitle
                }
                textConfirmation={Strings.common.thisActionIsIrreversible}
                textIcon={Strings.manufacture.partNumber.create.deletePartLabel}
              />
            </StyledContainerItems>
            <StyledContainerFooterSideBar>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setOpenSideBar(false);
                  setUsersSelected([]);
                }}
              >
                {Strings.common.cancel}
              </button>
              <Button
                variant="contained"
                size="large"
                className="btn-save"
                onClick={() => handleSubmit()}
              >
                {Strings.common.save}
              </Button>
            </StyledContainerFooterSideBar>
          </>
        )}
      </Formik>
    )
  );
};
